import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import { CELL } from '../core/config';
import { airframe } from '../world/airport';
import type { Helipad } from '../world/helipads';
import type { City } from '../world/city';
import type { Cluster, PhysicsCity } from '../physics/world';
import type { Person, Population } from './population';

type State =
  | 'ready'
  | 'outbound'
  | 'rescue approach'
  | 'hoisting'
  | 'observation'
  | 'returning'
  | 'handoff'
  | 'landing'
  | 'turnaround'
  | 'unavailable'
  | 'wreck';
interface Leg {
  point: THREE.Vector3;
  speed: number;
}
interface AirUnit {
  id: string;
  pad: Helipad;
  clusters: Cluster[];
  p: THREE.Vector3;
  q: THREE.Quaternion;
  velocity: THREE.Vector3;
  speed: number;
  state: State;
  reason: string;
  originalCells: number;
  envelope: { x: number; y: number; z: number };
  rotor: number;
  route: Leg[];
  timer: number;
  blocked: number;
  patient?: Person;
  patientFrom?: THREE.Vector3;
  incident?: string;
  target?: THREE.Vector3;
  destination?: Helipad;
  deliveries: number;
  observations: number;
}
const v = (p: { x: number; y: number; z: number }) => new THREE.Vector3(p.x, p.y, p.z);
const anchor = new THREE.Vector3(17.5, 0, 10),
  rotorAnchor = new THREE.Vector3(18.05, 6.25, 10.55);
/** Two shared-incident support aircraft. No new patients, no off-screen teleport, no infinite fleet. */
export class AirSupport {
  units: AirUnit[] = [];
  log: { time: number; unit: string; action: string; person?: string; incident?: string }[] = [];
  private time = 0;
  private age = 0;
  private epoch = 0;
  private observed = new Set<string>();
  private cables = new THREE.LineSegments(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({ color: 0x536663 }),
  );
  private cableData = new Float32Array(12);
  readonly cruise: number;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.cruise = Math.max(140, ...city.buildings.map((b) => (b.baseY ?? 0.16) + b.h * CELL + 35));
    this.cables.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(this.cableData, 3).setUsage(THREE.DynamicDrawUsage),
    );
    this.cables.frustumCulled = false;
    physics.scene.add(this.cables);
    this.reset();
  }
  private record(u: AirUnit, action: string): void {
    this.log.push({ time: this.time, unit: u.id, action, person: u.patient?.id, incident: u.incident });
    if (this.log.length > 512) this.log.shift();
  }
  clear(): void {
    for (const u of this.units) {
      this.population.fire.unregister(u.id);
      this.physics.unregisterRuntimeStructure(u.clusters[0].building.id);
      this.population.services?.reservations.release(u.id);
      if (u.patient) {
        u.patient.hidden = false;
        u.patient.controlled = false;
      }
    }
    this.units = [];
    this.log = [];
    this.observed.clear();
    this.time = this.age = 0;
    this.cables.geometry.setDrawRange(0, 0);
  }
  reset(): void {
    this.clear();
    this.epoch = this.physics.life.epoch;
    for (const [i, pad] of (this.city.helipads ?? []).entries()) {
      const b = airframe(910000 + i, 'helicopter');
      b.name = pad.role === 'medical' ? 'Riverside air ambulance' : 'Civic observation helicopter';
      for (const c of b.cells)
        if (c.material !== 'glass' && c.y > 0 && c.y < 4 && c.z < 10)
          c.color = pad.role === 'medical' ? 0xdddccd : 0x698e9e;
      const groups = [b.cells.filter((c) => c.y !== 5), b.cells.filter((c) => c.y === 5)];
      const clusters = this.physics.registerRuntimeAssembly(b, groups);
      for (const c of clusters) {
        c.body.setBodyType(RAPIER.RigidBodyType.KinematicPositionBased, true);
        c.body.enableCcd(true);
      }
      const u: AirUnit = {
        id: `helicopter:${pad.role}`,
        pad,
        clusters,
        p: v(pad.centre),
        q: new THREE.Quaternion(),
        velocity: new THREE.Vector3(),
        speed: 0,
        state: 'ready',
        reason: 'Crew ready at designated pad',
        originalCells: b.cells.length,
        envelope: (() => {
          const radius =
            Math.max(
              ...b.cells.map((c) => Math.hypot((c.x + 0.5) * CELL - anchor.x, (c.z + 0.5) * CELL - anchor.z)),
            ) + CELL;
          return { x: radius, y: Math.max(...b.cells.map((c) => (c.y + 1) * CELL)) / 2 + 0.2, z: radius };
        })(),
        rotor: 0,
        route: [],
        timer: 0,
        blocked: 0,
        deliveries: 0,
        observations: 0,
      };
      this.units.push(u);
      this.position(u, true);
      let heatAnchor = b.cells.find((c) => c.y === 2 && c.z === 8)?.id ?? b.cells[0].id;
      this.population.fire.register({
        id: u.id,
        category: 'vehicle',
        position: () => {
          let point = this.physics.cellPosition(heatAnchor);
          if (!point) {
            const cell = this.physics.buildingCells(b.id)[0];
            if (cell) {
              heatAnchor = cell.id;
              point = this.physics.cellPosition(heatAnchor);
            }
          }
          return point ?? u.p;
        },
        burnSeconds: 80,
        fuel: () => (this.physics.buildingCells(b.id).length ? 1 : 0),
        onHeat: (heat) => {
          if (heat > 0.85) this.wreck(u, 'Fire damaged aircraft');
        },
      });
    }
  }
  private position(u: AirUnit, initial = false): void {
    for (const [i, c] of u.clusters.entries()) {
      const rotorQ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), u.rotor);
      const local = c.origin.clone();
      if (i) local.sub(rotorAnchor).applyQuaternion(rotorQ).add(rotorAnchor);
      const p = local.sub(anchor).applyQuaternion(u.q).add(u.p),
        q = i ? u.q.clone().multiply(rotorQ) : u.q;
      if (initial) {
        c.body.setTranslation(p, true);
        c.body.setRotation(q, true);
        c.previous.copy(p);
        c.previousQ.copy(q);
      }
      c.body.setNextKinematicTranslation(p);
      c.body.setNextKinematicRotation(q);
      c.building.x = u.p.x - anchor.x;
      c.building.z = u.p.z - anchor.z;
    }
    if (initial) this.physics.world.propagateModifiedBodyPositionsToColliders();
  }
  private padOpen(pad: Helipad): boolean {
    return (
      this.population.services!.available(pad.facility) &&
      pad.supports.every((id) => {
        const owner = this.physics.cellOwner.get(id);
        return (
          owner !== undefined &&
          !!(this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner))?.anchored
        );
      })
    );
  }
  private columnOpen(point: THREE.Vector3, unit?: AirUnit): boolean {
    return this.physics.flightColumnClear(point, this.cruise - point.y + 10, unit?.clusters[0].building.id);
  }
  private rescueTarget(unit: AirUnit, point: THREE.Vector3): THREE.Vector3 | undefined {
    // A clear cable does not prove that a rotor and tail fit beside the tower.
    // Raise the real hover destination until the complete rotating envelope
    // has a clear descent from cruise. The cable still follows its own column.
    for (let height = 16; point.y + height <= this.cruise + 16; height += 8) {
      const target = point.clone().add(new THREE.Vector3(3, height, 1));
      if (
        this.physics.flightColumnClear(
          target,
          Math.max(1, this.cruise - target.y + 10),
          unit.clusters[0].building.id,
          unit.envelope,
        )
      )
        return target;
    }
    return;
  }
  private go(u: AirUnit, target: THREE.Vector3): void {
    const direct = target.clone().sub(u.p);
    if (
      direct.length() < 40 &&
      this.columnOpen(target, u) &&
      u.clusters.every((c) => !this.physics.sweepCluster(c, direct))
    ) {
      // A clear handoff-to-pad approach uses its actual short corridor. The
      // previous route unnecessarily climbed back to cruise before landing.
      u.route = [{ point: target.clone(), speed: 5 }];
      u.blocked = 0;
      return;
    }
    u.route = [
      { point: u.p.clone().setY(this.cruise), speed: 9 },
      { point: target.clone().setY(this.cruise), speed: 32 },
      { point: target.clone(), speed: 5 },
    ];
    u.blocked = 0;
  }
  private dispatch(): void {
    const services = this.population.services!;
    for (const u of this.units) {
      if (u.state !== 'ready' || !this.padOpen(u.pad)) continue;
      if (u.pad.role === 'medical') {
        const casualty = this.population.people
          .filter((p) => !!services.airRescueRequest(p))
          .sort((a, b) => u.p.distanceTo(a.p) - u.p.distanceTo(b.p))
          .find((p) => {
            if (!this.columnOpen(p.p)) return false;
            const target = this.rescueTarget(u, p.p);
            if (!target || !services.reservations.acquire(`patient:${p.id}`, u.id, this.time, 1, 900))
              return false;
            u.target = target;
            return true;
          });
        if (!casualty || !u.target) continue;
        u.patient = casualty;
        u.patientFrom = casualty.p.clone();
        u.incident = [...services.incidents.values()].find((i) => i.people.has(casualty.id))?.id;
        u.state = 'outbound';
        u.reason = 'Reserved casualty with unmet ground access · approaching clear rescue column';
        this.go(u, u.target);
        this.record(u, 'Medical dispatch');
      } else {
        const i = [...services.incidents.values()].find(
          (i) => i.severity >= 3 && i.state !== 'complete' && !this.observed.has(i.id),
        );
        if (!i) continue;
        this.observed.add(i.id);
        u.incident = i.id;
        u.target = i.point.clone().add(new THREE.Vector3(0, this.cruise - i.point.y, 45));
        u.state = 'outbound';
        u.reason = 'Major incident observation · road hazard assessment';
        this.go(u, u.target);
        this.record(u, 'Observation dispatch');
      }
    }
  }
  private returnUnit(u: AirUnit): void {
    let pad = u.pad;
    if (!this.padOpen(pad)) {
      const alternate = this.city.helipads?.find(
        (p) =>
          this.padOpen(p) && !this.units.some((other) => other !== u && other.p.distanceTo(v(p.centre)) < 35),
      );
      if (!alternate) {
        u.state = 'unavailable';
        u.route = [];
        u.reason = 'No safe supported landing pad · holding at last clear position';
        this.record(u, u.reason);
        return;
      }
      pad = alternate;
    }
    u.state = 'returning';
    u.destination = pad;
    const destination = u.patient ? v(pad.handoff).add(new THREE.Vector3(3, 16, 1)) : v(pad.centre);
    u.target = destination;
    this.go(u, destination);
    u.reason = u.patient
      ? 'Returning original casualty to road-connected handoff'
      : 'Role complete · returning to pad';
    this.record(u, 'Returning to service base');
  }
  private wreck(u: AirUnit, reason: string): void {
    if (u.state === 'wreck') return;
    u.state = 'wreck';
    u.reason = reason;
    u.route = [];
    this.population.services!.reservations.release(u.id);
    for (const c of this.physics.clusters.values())
      if (c.building.id === u.clusters[0].building.id) {
        c.anchored = false;
        c.body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
        c.body.setLinvel(u.velocity, true);
        c.body.enableCcd(true);
      }
    if (u.patient) {
      const p = u.patient;
      const aboard = p.hidden;
      p.hidden = false;
      p.controlled = false;
      p.state = 'incapacitated';
      if (aboard) {
        p.p.copy(u.p);
        p.previous.copy(p.p);
        p.velocity.copy(u.velocity);
      }
      this.population.services!.relocateCasualty(p);
      u.patient = undefined;
    }
    this.physics.blast(u.p, 7, 8, true, 'impact', 0.8);
    this.record(u, reason);
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) return;
    this.time += dt;
    this.age += dt;
    if (this.age >= 1) {
      this.age = 0;
      this.dispatch();
    }
    for (const u of this.units) {
      if (u.state === 'wreck') continue;
      if (u.patient?.fatal) {
        const patient = u.patient;
        patient.hidden = patient.controlled = false;
        patient.state = 'incapacitated';
        this.population.services!.reservations.release(u.id);
        u.patient = undefined;
        u.patientFrom = undefined;
        this.record(u, 'Direct projectile casualty; rescue assignment released');
        this.returnUnit(u);
      }
      if (
        u.clusters.some((c) => !this.physics.clusters.has(c.id)) ||
        this.physics.buildingCells(u.clusters[0].building.id).length < u.originalCells
      ) {
        this.wreck(u, 'Damaged airframe · controlled flight lost');
        continue;
      }
      if (u.patient) {
        this.population.services!.reservations.acquire(`patient:${u.patient.id}`, u.id, this.time, 1, 900);
        if (u.patient.hidden) {
          u.patient.previous.copy(u.patient.p);
          u.patient.p.copy(u.p);
        }
      }
      if (u.state === 'ready') {
        if (!this.padOpen(u.pad)) {
          u.state = 'unavailable';
          u.reason = 'Base or pad damaged · aircraft unavailable';
        }
        continue;
      }
      if (u.state === 'unavailable') continue;
      u.rotor = (u.rotor + dt * 38) % (Math.PI * 2);
      if (u.state === 'turnaround') {
        u.timer -= dt;
        if (u.timer <= 0) {
          u.state = this.padOpen(u.pad) ? 'ready' : 'unavailable';
          u.reason = 'Crew and fuel turnaround complete';
          this.record(u, 'Ready after return');
        }
      } else if (u.route.length) {
        const leg = u.route[0],
          delta = leg.point.clone().sub(u.p),
          distance = delta.length(),
          // Recorded pre-revision climb/cruise/descent settings are 9/32/5.
          // Regional emergency travel applies 5x once, with real acceleration
          // and stopping distance; the last 20m of descent stays controlled.
          requested = Math.min(
            leg.speed * (this.city.regional ? 5 : 1),
            Math.sqrt(2 * 12 * distance),
            leg === u.route.at(-1) && distance < 20 ? 5 : Infinity,
          );
        u.speed += THREE.MathUtils.clamp(requested - u.speed, -12 * dt, 8 * dt);
        const step = Math.min(distance, u.speed * dt),
          move = delta.normalize().multiplyScalar(step);
        const horizontal = move.clone().setY(0);
        if (horizontal.lengthSq() > 0.00001) {
          const q = new THREE.Quaternion().setFromUnitVectors(
            new THREE.Vector3(0, 0, -1),
            horizontal.normalize(),
          );
          u.q.rotateTowards(q, dt * 0.8);
        }
        const hit = u.clusters.map((c) => this.physics.sweepCluster(c, move)).find(Boolean);
        if (hit) {
          u.speed = 0;
          u.blocked += dt;
          u.velocity.set(0, 0, 0);
          u.reason = 'Live geometry blocks safe flight · waiting';
          if (u.blocked > 8) {
            this.record(u, 'Actual aircraft collision blocked this flight segment');
            if (u.state === 'outbound') {
              if (u.patient) {
                this.population.services!.reservations.release(u.id);
                u.patient = undefined;
              }
              this.returnUnit(u);
            } else {
              u.route = [];
              u.state = 'unavailable';
              u.reason = 'Return corridor obstructed · unresolved at last safe position';
              this.record(u, u.reason);
            }
          }
        } else {
          u.blocked = 0;
          u.velocity.copy(move).divideScalar(dt);
          u.p.add(move);
          if (distance <= step + 0.001) {
            u.p.copy(leg.point);
            u.speed = 0;
            u.route.shift();
            if (!u.route.length) this.arrive(u);
          }
        }
      } else if (u.state === 'hoisting' || u.state === 'handoff') {
        const p = u.patient!;
        u.timer += dt;
        const up = u.state === 'hoisting';
        p.previous.copy(p.p);
        p.hidden = false;
        p.controlled = true;
        p.state = up ? 'air rescue hoist' : 'air rescue handoff';
        p.velocity.set(0, 0, 0);
        const hook = u.p.clone().add(new THREE.Vector3(-3, 1, -1));
        p.p
          .copy(up ? u.patientFrom! : hook)
          .lerp(up ? hook : v((u.destination ?? u.pad).handoff), Math.min(1, u.timer / 14));
        if (u.timer >= 14) {
          if (up) {
            p.hidden = true;
            p.state = 'aboard air ambulance';
            this.record(u, 'Original casualty lifted aboard');
            this.returnUnit(u);
          } else {
            p.hidden = false;
            p.controlled = false;
            p.state = 'incapacitated';
            p.layer = 'ground';
            p.previous.copy(p.p);
            this.population.services!.reservations.release(u.id);
            this.population.services!.relocateCasualty(p);
            u.deliveries++;
            this.record(u, 'Original casualty handed to ground medical service');
            u.patient = undefined;
            u.state = 'landing';
            this.go(u, v((u.destination ?? u.pad).centre));
          }
        }
      } else if (u.state === 'observation') {
        u.timer += dt;
        this.population.services!.observeIncident(u.incident!);
        if (u.timer >= 35) {
          u.observations++;
          this.record(u, 'Road hazard observation completed');
          this.returnUnit(u);
        }
      }
      this.position(u);
    }
  }
  private arrive(u: AirUnit): void {
    u.velocity.set(0, 0, 0);
    u.timer = 0;
    if (u.state === 'outbound') {
      if (u.patient) {
        if (
          u.patient.state !== 'incapacitated' ||
          u.patient.p.distanceTo(u.patientFrom!) > 0.75 ||
          !this.columnOpen(u.patient.p, u)
        ) {
          this.population.services!.reservations.release(u.id);
          u.patient = undefined;
          u.reason = 'Casualty moved or hoist column blocked';
          this.returnUnit(u);
          return;
        }
        u.state = 'hoisting';
        u.reason = 'Visible rescue hoist · original patient identity';
      } else {
        u.state = 'observation';
        u.reason = 'Assessing incident road access for coordinated closures';
      }
    } else if (u.patient) {
      u.state = 'handoff';
      u.reason = 'Lowering patient to reserved ground access';
    } else {
      u.state = 'turnaround';
      u.timer = 25;
      u.q.identity();
      u.reason = 'Landed · finite crew and fuel turnaround';
      this.record(u, 'Landed after role');
    }
  }
  render(): void {
    let count = 0;
    for (const u of this.units)
      if (u.patient && ['hoisting', 'handoff'].includes(u.state)) {
        this.cableData.set(
          [u.p.x - 2, u.p.y + 3, u.p.z - 1, u.patient.p.x, u.patient.p.y + 0.65, u.patient.p.z],
          count * 6,
        );
        count++;
      }
    this.cables.geometry.setDrawRange(0, count * 2);
    this.cables.geometry.getAttribute('position').needsUpdate = true;
  }
  snapshot(): Record<string, number> {
    return {
      helicopters: this.units.length,
      airRescues: this.units.reduce((n, u) => n + u.deliveries, 0),
      airObservations: this.units.reduce((n, u) => n + u.observations, 0),
      airUnitsReady: this.units.filter((u) => u.state === 'ready').length,
    };
  }
  inspect(): unknown {
    return {
      ...this.snapshot(),
      units: this.units.map((u) => ({
        speed: u.speed,
        id: u.id,
        state: u.state,
        reason: u.reason,
        pad: u.pad.id,
        position: u.p.toArray(),
        patient: u.patient?.id,
        incident: u.incident,
        deliveries: u.deliveries,
        observations: u.observations,
      })),
      log: this.log,
    };
  }
  dispose(): void {
    this.clear();
    this.physics.scene.remove(this.cables);
    this.cables.geometry.dispose();
    this.cables.material.dispose();
  }
}
